import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Grid,
  Chip, Tooltip, TablePagination, Autocomplete, CircularProgress, RadioGroup,
  FormControlLabel, Radio, Card, CardMedia, CardContent, Menu
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { collection, getDocs, doc, addDoc, updateDoc, deleteDoc, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { AuthContext } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { categoryMap, watchMaterialMap, jewelryMaterialMap, conditionMap, locationMap } from '../../utils/productMappings';

// --- CONSTANTS ---
const WORKSHOP_STATUS = {
  WAITING: { value: 'waiting', label: 'Oczekuje', color: 'warning' },
  IN_PROGRESS: { value: 'in_progress', label: 'W realizacji', color: 'info' },
  COMPLETED: { value: 'completed', label: 'Ukończone', color: 'success' },
  PICKED_UP: { value: 'picked_up', label: 'primary' },
  CANCELLED: { value: 'cancelled', label: 'Anulowane', color: 'error' }
};

const REPAIR_TYPES = [
  { value: 'sizing', label: 'Zmiana rozmiaru' },
  { value: 'stone_replacement', label: 'Wymiana kamienia' },
  { value: 'cleaning', label: 'Czyszczenie' },
  { value: 'polishing', label: 'Polerowanie' },
  { value: 'repair', label: 'Naprawa' },
  { value: 'other', label: 'Inne' }
];

const PRIORITY_LEVELS = {
  STANDARD: { value: 'standard', label: 'Standardowe', color: 'info' },
  URGENT: { value: 'urgent', label: 'Pilne', color: 'warning' },
  VERY_URGENT: { value: 'very_urgent', label: 'Bardzo pilne', color: 'error' }
};

const getProductDisplayName = (product) => {
  if (!product) return '';
  if (product.brand) {
    return `${product.brand} ${product.model || ''}`.trim();
  }
  return product.jewelry_name || product.name || 'Produkt bez nazwy';
};

// --- MAIN COMPONENT ---
const Workshop = () => {
    const { currentUser } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const isWorkshopRole = currentUser?.role === 'Pracownia';
  const isAdmin = currentUser?.role === 'admin';

  // --- STATE MANAGEMENT ---
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [sourceType, setSourceType] = useState('stock');

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  const [allProducts, setAllProducts] = useState([]);
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchFilters, setSearchFilters] = useState({ sku: '', brand: '', model: '' });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);

  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState(null);
  const [selectedItemForStatusChange, setSelectedItemForStatusChange] = useState(null);

  const initialFormData = {
    product_id: '',
    product_name: '',
    product_sku: '',
    repair_type: 'other',
    description: '',
    estimated_completion: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    deadline: '',
    priority: 'standard',
    cost: '',
    status: 'waiting',
    notes: '',
    client_id: '',
    client_name: '',
    client_phone: '',
  };
  const [formData, setFormData] = useState(initialFormData);

  // --- DATA FETCHING ---
  const fetchWorkshopItems = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const workshopCollectionRef = collection(db, 'workshop');
      let workshopQuery;

      if (isAdmin) {
        workshopQuery = query(workshopCollectionRef);
      } else {
        workshopQuery = query(workshopCollectionRef, where('employee_id', '==', currentUser.uid));
      }

      const querySnapshot = await getDocs(workshopQuery);
      const itemsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          deadline: data.deadline && typeof data.deadline.toDate === 'function' ? data.deadline.toDate() : null,
          estimated_completion: data.estimated_completion && typeof data.estimated_completion.toDate === 'function' ? data.estimated_completion.toDate() : null,
        };
      });
      setItems(itemsData.sort((a, b) => (b.created_at?.toDate() || 0) - (a.created_at?.toDate() || 0)));
    } catch (error) {
      console.error("Error fetching workshop items: ", error);
      enqueueSnackbar('Błąd podczas pobierania zleceń z pracowni: ' + error.message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [currentUser, isAdmin, enqueueSnackbar]);

  const fetchClients = useCallback(async () => {
    if (!currentUser) return;
    try {
      let clientsQuery = collection(db, 'clients');
      if (currentUser.role !== 'admin') {
        clientsQuery = query(clientsQuery, where('added_by', '==', currentUser.uid));
      }
      const clientsSnapshot = await getDocs(clientsQuery);
      setClients(clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Error fetching clients: ", error);
    }
  }, [currentUser]);

  const fetchProducts = useCallback(async () => {
    try {
      const productsSnapshot = await getDocs(query(collection(db, 'products'), where('status', '==', 'available')));
      const productsList = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllProducts(productsList);

      const uniqueBrands = [...new Set(productsList.map(p => p.brand).filter(Boolean))].sort();
      setBrandOptions(uniqueBrands);
    } catch (error) {
      console.error("Error fetching products: ", error);
    }
  }, []);

  useEffect(() => {
    fetchWorkshopItems();
    if (!isWorkshopRole) {
      fetchClients();
      fetchProducts();
    }
  }, [fetchWorkshopItems, fetchClients, fetchProducts, isWorkshopRole]);

  // --- FORM & DIALOG HANDLERS ---
  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedItem(null);
    setSelectedClient(null);
    setSelectedProduct(null);
    setSourceType('stock');

    setProductSearchResults([]);
    setSearchFilters({ sku: '', brand: '', model: '' });
  };

  const handleAddItem = () => {
    resetForm();
    setOpenDialog(true);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);

    // Sanitize and format data for the form
    const formattedData = {
      ...initialFormData, // Start with initial structure to ensure all keys are present
      ...Object.entries(item).reduce((acc, [key, value]) => {
        acc[key] = value === null || value === undefined ? '' : value; // Replace null/undefined with empty string
        return acc;
      }, {}),
      estimated_completion: item.estimated_completion ? format(new Date(item.estimated_completion), 'yyyy-MM-dd') : '',
      deadline: item.deadline ? format(new Date(item.deadline), 'yyyy-MM-dd') : '',
    };

    setFormData(formattedData);
    if (item.client_id) {
      const client = clients.find(c => c.id === item.client_id);
      if (client) setSelectedClient(client);
    }

    if (item.product_id) {
      const product = allProducts.find(p => p.id === item.product_id);
      if (product) {
        setSelectedProduct(product);
      }
    } else {
      setSelectedProduct(null);
    }

    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    resetForm();
    setSelectedProduct(null);
    setSelectedClient(null);
  };

  const handleSubmit = async () => {
    let finalFormData = { ...formData };

    if (!isWorkshopRole) {
      if (sourceType === 'stock' && !selectedProduct) return enqueueSnackbar('Wybierz produkt z magazynu.', { variant: 'error' });
      if (sourceType === 'client' && !finalFormData.product_name) return enqueueSnackbar('Podaj nazwę produktu klienta.', { variant: 'error' });
      if (!selectedClient) return enqueueSnackbar('Wybierz klienta z listy.', { variant: 'error' });
      finalFormData.client_id = selectedClient.id;
      finalFormData.client_name = selectedClient.name;
      finalFormData.client_phone = selectedClient.phone;
    }

    finalFormData.cost = parseFloat(finalFormData.cost) || 0;
    finalFormData.employee_id = currentUser.uid;
    finalFormData.employee_name = currentUser.displayName;
    if (finalFormData.estimated_completion) finalFormData.estimated_completion = Timestamp.fromDate(new Date(finalFormData.estimated_completion));
    if (finalFormData.deadline) finalFormData.deadline = Timestamp.fromDate(new Date(finalFormData.deadline));

    try {
      if (selectedItem) {
        const itemDoc = doc(db, 'workshop', selectedItem.id);
        await updateDoc(itemDoc, { ...finalFormData, updated_at: Timestamp.now() });
        enqueueSnackbar('Zlecenie zaktualizowane!', { variant: 'success' });
      } else {
        await addDoc(collection(db, 'workshop'), { ...finalFormData, created_at: Timestamp.now() });
        if (sourceType === 'stock' && selectedProduct) {
          await updateDoc(doc(db, 'products', selectedProduct.id), { status: 'in_workshop' });
        }
        enqueueSnackbar('Nowe zlecenie dodane!', { variant: 'success' });
      }
      fetchWorkshopItems();
      handleDialogClose();
    } catch (error) {
      console.error("Error submitting form: ", error);
      enqueueSnackbar('Wystąpił błąd podczas zapisu.', { variant: 'error' });
    }
  };

  const handleStatusMenuOpen = (event, item) => {
    setStatusMenuAnchorEl(event.currentTarget);
    setSelectedItemForStatusChange(item);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchorEl(null);
    setSelectedItemForStatusChange(null);
  };

  const handleQuickStatusChange = async (newStatus) => {
    if (!selectedItemForStatusChange) return;

    const itemRef = doc(db, 'workshop', selectedItemForStatusChange.id);
    try {
      await updateDoc(itemRef, { status: newStatus });

      // If status is 'completed' or 'cancelled', update product status back to 'available'
      if ((newStatus === 'completed' || newStatus === 'cancelled') && selectedItemForStatusChange.product_id) {
        const productRef = doc(db, 'products', selectedItemForStatusChange.product_id);
        await updateDoc(productRef, { status: 'available' });
      }

      enqueueSnackbar('Status zaktualizowany pomyślnie!', { variant: 'success' });
      fetchWorkshopItems(); // Refresh the list
    } catch (error) {
      console.error("Error updating status: ", error);
      enqueueSnackbar('Błąd podczas aktualizacji statusu.', { variant: 'error' });
    }
    handleStatusMenuClose();
  };

  const handleDeleteItem = async (item) => {
    if (window.confirm('Czy na pewno chcesz usunąć to zlecenie?')) {
      try {
        await deleteDoc(doc(db, 'workshop', item.id));
        if (item.product_id && item.source === 'stock') { // Assuming 'source' field exists
          await updateDoc(doc(db, 'products', item.product_id), { status: 'available' });
        }
        enqueueSnackbar('Zlecenie usunięte.', { variant: 'info' });
        fetchWorkshopItems();
      } catch (error) {
        console.error('Error deleting item:', error);
        enqueueSnackbar('Błąd podczas usuwania zlecenia.', { variant: 'error' });
      }
    }
  };

  // --- PRODUCT SEARCH HANDLERS ---
  const handleProductSearch = () => {
    let results = allProducts;
    if (searchFilters.sku) results = results.filter(p => p.sku?.toLowerCase().includes(searchFilters.sku.toLowerCase()));
    if (searchFilters.brand) results = results.filter(p => p.brand === searchFilters.brand);
    if (searchFilters.model) results = results.filter(p => p.model?.toLowerCase().includes(searchFilters.model.toLowerCase()));
    setProductSearchResults(results);
  };

  const handleResetSearch = () => {
    setSearchFilters({ sku: '', brand: '', model: '' });
    setProductSearchResults([]);
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setFormData(prev => ({ ...prev, product_id: product.id, product_name: getProductDisplayName(product), product_sku: product.sku || '' }));
    setProductSearchResults([]);
  };

  const handleChangeProduct = () => {
    setSelectedProduct(null);
    setFormData(prev => ({ ...prev, product_id: '', product_name: '', product_sku: '' }));
  };

  // --- RENDER HELPERS ---
  const renderProductCard = (product, isSelectable) => {
    const translateValue = (map, value) => map?.[value] || value || '';
    const renderInfoField = (label, value) => (value ? <Typography variant="body2" color="text.secondary">{label}: {value}</Typography> : null);

    return (
      <Card
        sx={{
          height: '100%', display: 'flex', flexDirection: 'column', cursor: isSelectable ? 'pointer' : 'default',
          bgcolor: '#000000', color: 'white', border: '1px solid #333',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': isSelectable ? { transform: 'translateY(-5px)', boxShadow: '0 4px 20px 0 rgba(255, 215, 0, 0.3)' } : {}
        }}
        onClick={isSelectable ? () => handleSelectProduct(product) : undefined}
      >
        <CardMedia component="div" sx={{ position: 'relative', paddingTop: '75%', backgroundColor: '#000' }}>
          <img src={product.images?.[0] || 'https://via.placeholder.com/300'} alt={getProductDisplayName(product)} style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', top: 0, left: 0 }} />
        </CardMedia>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
          <Typography variant="h6" component="h2" fontWeight="bold" noWrap>{getProductDisplayName(product)}</Typography>
          <Box flexGrow={1} mt={1}>
            {product.brand ? (
              <>
                {renderInfoField('Materiał', translateValue(watchMaterialMap, product.material))}
                {renderInfoField('Stan', translateValue(conditionMap, product.condition))}
                {renderInfoField('Rok', product.production_year)}
              </>
            ) : (
              <>
                {renderInfoField('Materiał', translateValue(jewelryMaterialMap, product.material))}
                {product.material !== 'platinum' && renderInfoField('Próba', product.gold_fineness)}
                {renderInfoField('Waga', product.weight_grams ? `${product.weight_grams} g` : null)}
              </>
            )}
            {renderInfoField('ID', product.sku)}
          </Box>
          <Typography variant="h5" component="p" sx={{ color: '#FFD700', fontWeight: 'bold' }}>{`${product.price ? product.price.toLocaleString() : 0} PLN`}</Typography>
        </CardContent>
      </Card>
    );
  };

  // --- MAIN RENDER ---
  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" gutterBottom>Pracownia</Typography>
          {!isWorkshopRole && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddItem}>Dodaj zlecenie</Button>
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Produkt</TableCell>
              <TableCell>Klient</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priorytet</TableCell>
              <TableCell>Pracownik</TableCell>
              <TableCell>Termin</TableCell>
              <TableCell>Deadline</TableCell>
              <TableCell>Akcje</TableCell>
              {(currentUser.role === 'admin' || currentUser.role === 'Pracownia') && <TableCell>Zmień Status</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress /></TableCell></TableRow>
            ) : (
              items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.product_name}</TableCell>
                  <TableCell>{item.client_name} ({item.client_phone})</TableCell>
                  <TableCell><Chip label={WORKSHOP_STATUS[item.status?.toUpperCase()]?.label || item.status} color={WORKSHOP_STATUS[item.status?.toUpperCase()]?.color || 'default'} size="small" /></TableCell>
                  <TableCell><Chip label={PRIORITY_LEVELS[item.priority?.toUpperCase()]?.label || item.priority} color={PRIORITY_LEVELS[item.priority?.toUpperCase()]?.color || 'default'} size="small" /></TableCell>
                  <TableCell>{item.employee_name}</TableCell>
                  <TableCell>{item.estimated_completion ? format(item.estimated_completion, 'dd.MM.yyyy') : 'Brak'}</TableCell>
                  <TableCell>{item.deadline ? format(item.deadline, 'dd.MM.yyyy') : 'Brak'}</TableCell>
                  <TableCell>
                    <Tooltip title="Edytuj">
                      <IconButton onClick={() => handleEditItem(item)}><EditIcon /></IconButton>
                    </Tooltip>
                    {!isWorkshopRole && (
                      <Tooltip title="Usuń">
                        <IconButton onClick={() => handleDeleteItem(item)} disabled={!isAdmin}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  {(currentUser.role === 'admin' || currentUser.role === 'Pracownia') && (
                    <TableCell>
                      <Tooltip title="Zmień status">
                        <IconButton onClick={(e) => handleStatusMenuOpen(e, item)}>
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination rowsPerPageOptions={[10, 25, 50]} component="div" count={items.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, newPage) => setPage(newPage)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
      </TableContainer>

      <Dialog open={openDialog} onClose={handleDialogClose} fullWidth maxWidth="md">
        <DialogTitle>{selectedItem ? 'Edytuj zlecenie' : 'Nowe zlecenie w pracowni'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {isWorkshopRole ? (
                <Grid item xs={12}><Typography variant="h6">Edycja statusu zlecenia</Typography></Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <RadioGroup row value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
                      <FormControlLabel value="stock" control={<Radio />} label="Produkt z magazynu" />
                      <FormControlLabel value="client" control={<Radio />} label="Produkt klienta" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {sourceType === 'stock' ? (
                  <Grid item xs={12}>
                    {selectedProduct ? (
                      <Box>
                        <Typography variant="subtitle1" gutterBottom>Wybrany produkt:</Typography>
                        {renderProductCard(selectedProduct, false)}
                        <Button variant="outlined" onClick={handleChangeProduct} sx={{ mt: 2 }}>Zmień produkt</Button>
                      </Box>
                    ) : (
                      <>
                        <Typography variant="subtitle2">Wyszukaj produkt w magazynie</Typography>
                        <Grid container spacing={2} alignItems="center" sx={{mt: 1}}>
                          <Grid item xs={12} sm={4}><TextField fullWidth label="SKU / ID" value={searchFilters.sku} onChange={(e) => setSearchFilters(p => ({...p, sku: e.target.value}))} /></Grid>
                          <Grid item xs={12} sm={4}>
                            <Autocomplete options={brandOptions} getOptionLabel={(o) => o} value={searchFilters.brand || null} onChange={(e, val) => { setSearchFilters(p => ({...p, brand: val, model: ''})); setModelOptions(val ? [...new Set(allProducts.filter(p => p.brand === val).map(p => p.model).filter(Boolean))].sort() : []); }} renderInput={(params) => <TextField {...params} label="Marka" />} />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Autocomplete options={modelOptions} getOptionLabel={(o) => o} value={searchFilters.model || null} onChange={(e, val) => setSearchFilters(p => ({...p, model: val}))} renderInput={(params) => <TextField {...params} label="Model" />} disabled={!searchFilters.brand} />
                          </Grid>
                          <Grid item xs={6}><Button fullWidth variant="contained" onClick={handleProductSearch} startIcon={<SearchIcon />}>Szukaj</Button></Grid>
                          <Grid item xs={6}><Button fullWidth variant="outlined" onClick={handleResetSearch}>Resetuj</Button></Grid>
                        </Grid>
                        <Box sx={{ mt: 2, maxHeight: 400, overflowY: 'auto' }}>
                          <Grid container spacing={2} sx={{ mt: 1 }}>
                            {productSearchResults.map(p => <Grid item key={p.id} xs={12} sm={6} md={4}>{renderProductCard(p, true)}</Grid>)}
                          </Grid>
                        </Box>
                      </>
                    )}
                  </Grid>
                ) : (
                    <>
                        <Grid item xs={12} md={6}><TextField fullWidth label="Nazwa produktu klienta" value={formData.product_name} onChange={e => setFormData(p => ({...p, product_name: e.target.value}))} /></Grid>
                        <Grid item xs={12} md={6}><TextField fullWidth label="SKU (jeśli jest)" value={formData.product_sku} onChange={e => setFormData(p => ({...p, product_sku: e.target.value}))} /></Grid>
                    </>
                )}

                <Grid item xs={12}><hr/></Grid>

                <Grid item xs={12}>
                  <Autocomplete 
                    options={clients} 
                    getOptionLabel={(c) => `${c.name} (${c.phone || 'brak tel.'})`} 
                    value={selectedClient} 
                    onChange={(e, val) => setSelectedClient(val)} 
                    renderInput={(params) => <TextField {...params} label="Wyszukaj klienta" />} 
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}><hr/></Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Typ naprawy</InputLabel>
                <Select value={formData.repair_type} label="Typ naprawy" onChange={e => setFormData(p => ({...p, repair_type: e.target.value}))} disabled={isWorkshopRole}>
                  {REPAIR_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Koszt" value={formData.cost} onChange={e => setFormData(p => ({...p, cost: e.target.value}))} disabled={isWorkshopRole} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Opis zlecenia" value={formData.description} onChange={e => setFormData(p => ({...p, description: e.target.value}))} disabled={isWorkshopRole} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth type="date" label="Przewidywany termin" value={formData.estimated_completion} onChange={e => setFormData(p => ({...p, estimated_completion: e.target.value}))} InputLabelProps={{ shrink: true }} disabled={isWorkshopRole} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth type="date" label="Deadline" value={formData.deadline} onChange={e => setFormData(p => ({...p, deadline: e.target.value}))} InputLabelProps={{ shrink: true }} disabled={isWorkshopRole} /></Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priorytet</InputLabel>
                <Select value={formData.priority} label="Priorytet" onChange={e => setFormData(p => ({...p, priority: e.target.value}))} disabled={isWorkshopRole}>
                  {Object.values(PRIORITY_LEVELS).map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={formData.status} label="Status" onChange={e => setFormData(p => ({...p, status: e.target.value}))}>
                  {Object.values(WORKSHOP_STATUS).map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Notatki" value={formData.notes} onChange={e => setFormData(p => ({...p, notes: e.target.value}))} /></Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Anuluj</Button>
          <Button onClick={handleSubmit} variant="contained">{selectedItem ? 'Zapisz zmiany' : 'Dodaj zlecenie'}</Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={statusMenuAnchorEl}
        open={Boolean(statusMenuAnchorEl)}
        onClose={handleStatusMenuClose}
      >
        {Object.entries(WORKSHOP_STATUS).map(([key, { label }]) => (
          <MenuItem key={key} onClick={() => handleQuickStatusChange(key.toLowerCase())}>
            {label}
          </MenuItem>
        ))}
      </Menu>

    </Box>
  );
};

export default Workshop;
